import { Flex, Text } from '@bitrise/bitkit';
import { VizContext } from '@bitrise/bitviz';
import { SessionSpan } from '@trace/types';
import classnames from 'classnames';
import React, {
  CSSProperties,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  FunctionComponent,
} from 'react';
import { formatterDuration } from '../../../utils/getFormattedAttributeValue';
import { PADDING_TOP } from './SessionTraceVisualisation';
import SessionTraceVisualisationTooltip from './SessionTraceVisualisationTooltip';

export const SIZE_HEIGHT_COLLAPSED = 4;
export const SIZE_HEIGHT_EXPANDED = 32;
export const SIZE_GAP = 8;

interface Props {
  index: number;
  span: SessionSpan;
}

const SessionTraceVisualisationSpan: FunctionComponent<Props> = (props) => {
  const { index, span } = props;

  const { artboardRight, artboardWidth, getX, getX0 } = useContext(VizContext);

  const classes = classnames('TraceSpans__span', {
    'TraceSpans__span--root': !span.parentSpanId,
  });

  const ref = useRef<HTMLElement>();
  const [styleLabel, setStyleLabel] = useState<CSSProperties>({});
  const y = PADDING_TOP * 2 + (SIZE_HEIGHT_EXPANDED + SIZE_GAP) * index;
  const px = getX0(span.start) as number;
  const width = Math.max(8, (getX(span.end) as number) - px);

  useLayoutEffect(() => {
    if (ref.current && ref.current.clientWidth > width) {
      if (px + width + ref.current.clientWidth > artboardRight) {
        setStyleLabel({ transform: 'translateX(-100%)' });
      } else {
        setStyleLabel({ transform: `translateX(${width}px)` });
      }
    } else {
      setStyleLabel({});
    }
  }, [artboardRight, px, width]);

  return (
    <g className={classes} transform={`translate(0, ${y})`}>
      <rect
        className="TraceSpans__span-background"
        height={SIZE_HEIGHT_EXPANDED + SIZE_GAP}
        width={artboardWidth + SIZE_GAP * 2}
        x={-SIZE_GAP}
        y={SIZE_GAP * -0.5}
      />

      <SessionTraceVisualisationTooltip span={span}>
        {(tooltipProps) => (
          <g {...tooltipProps}>
            <rect
              className="TraceSpans__span-bar"
              height={SIZE_HEIGHT_EXPANDED}
              rx="4"
              width={width}
              x={px}
              y="0"
            />

            <foreignObject
              height={SIZE_HEIGHT_EXPANDED}
              style={{ overflow: 'visible' }}
              width={width}
              x={px}
            >
              <div>
                <Flex
                  alignChildrenVertical="middle"
                  container
                  direction="horizontal"
                  height={SIZE_HEIGHT_EXPANDED}
                  onPointerEnter={tooltipProps.onPointerEnter}
                  onPointerLeave={tooltipProps.onPointerLeave}
                  style={{ position: 'fixed' }}
                >
                  <Flex>
                    <Text
                      breakOn="none"
                      innerRef={ref}
                      paddingHorizontal="x2"
                      size="x2"
                      style={styleLabel}
                    >
                      <Text inline weight="bold">
                        {span.name}
                      </Text>{' '}
                      ({formatterDuration(span.end - span.start)})
                    </Text>
                  </Flex>
                </Flex>
              </div>
            </foreignObject>
          </g>
        )}
      </SessionTraceVisualisationTooltip>
    </g>
  );
};

export default SessionTraceVisualisationSpan;
